import { Link } from "react-router-dom";
import { Gauge, Calendar as CalIcon, Fuel, TrendingDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatPrice, formatMileage } from "@/lib/api";

export default function CarCard({ car, testIdPrefix = "car-card" }) {
  const img = car.image_urls && car.image_urls[0];
  const isSold = car.status === "sold";
  const isComingSoon = car.status === "coming_soon";
  const priceReduced = !isSold && car.previous_price && car.previous_price > car.price;

  return (
    <Link
      to={`/inventory/${car.id}`}
      data-testid={`${testIdPrefix}-${car.id}`}
      className="group relative block overflow-hidden border border-soft bg-card hover:border-accent transition-all duration-300"
    >
      {isSold && (
        <span
          data-testid={`${testIdPrefix}-sold-ribbon-${car.id}`}
          className="absolute top-5 -right-9 z-10 rotate-45 bg-accent text-accent-foreground text-xs font-bold uppercase tracking-[0.3em] py-1.5 w-36 text-center shadow-md"
        >
          Sold
        </span>
      )}
      {img && (
        <div className="aspect-[16/10] overflow-hidden bg-secondary">
          <img
            src={img}
            alt={`${car.year} ${car.make} ${car.model}`}
            className={`h-full w-full object-cover transition-transform duration-700 group-hover:scale-105 ${isSold ? "opacity-60 grayscale-[40%]" : ""}`}
            loading="lazy"
          />
        </div>
      )}
      <div className="p-6">
        <div className="flex items-center gap-2 mb-2">
          {isComingSoon && (
            <Badge
              data-testid={`${testIdPrefix}-coming-soon-${car.id}`}
              className="bg-gold text-primary hover:bg-gold border-none rounded-sm text-[10px] uppercase tracking-wider font-bold"
            >
              Coming Soon
            </Badge>
          )}
          {priceReduced && (
            <Badge
              data-testid={`${testIdPrefix}-price-reduced-${car.id}`}
              className="bg-accent text-accent-foreground hover:bg-accent border-none rounded-sm text-[10px] uppercase tracking-wider gap-1"
            >
              <TrendingDown className="h-3 w-3" /> Price Reduced
            </Badge>
          )}
          {car.featured && !isSold && (
            <Badge className="bg-primary text-primary-foreground hover:bg-primary border-none rounded-sm text-[10px] uppercase tracking-wider">
              Featured
            </Badge>
          )}
          {car.status === "pending" && (
            <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-wider">
              Sale Pending
            </Badge>
          )}
          {car.source === "import" && (
            <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-wider">
              Network
            </Badge>
          )}
        </div>
        <h3 className="font-serif text-2xl text-primary leading-tight">
          {car.year} {car.make} {car.model}
        </h3>
        <div className="mt-2 flex items-baseline gap-2">
          <span className={`text-2xl font-serif ${isSold ? "text-muted-foreground line-through" : "text-primary"}`}>
            {formatPrice(car.price)}
          </span>
          {priceReduced && (
            <span className="text-sm text-muted-foreground line-through" data-testid={`${testIdPrefix}-old-price-${car.id}`}>
              {formatPrice(car.previous_price)}
            </span>
          )}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Gauge className="h-3 w-3" /> {formatMileage(car.mileage)}
          </span>
          {car.fuel_type && (
            <span className="inline-flex items-center gap-1">
              <Fuel className="h-3 w-3" /> {car.fuel_type}
            </span>
          )}
          {car.transmission && (
            <span className="inline-flex items-center gap-1">
              <CalIcon className="h-3 w-3" /> {car.transmission}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
